import './Signup.css';
import '../common-style.css';

import { useContext, useEffect, useState } from 'react';
import { FetchError } from 't-lib-ui/connector';
import { validatePassword } from '../../schema-copy/security';
import { AppState } from '../../context/general';
import { EmailLength, PasswordLength } from '../../schema-copy/auth-schema';
import { useProgressModal, focusFirstElement } from 't-lib-ui/structure';
import { redirect, RouterContext, getLocation } from 't-lib-ui/router';
import { FlowForm, useFlowFormGlobal } from 't-lib-ui/structure';
import { Note } from 't-lib-ui/widgets';
import { Link } from 't-lib-ui/router';
import { Input, PasswordInput } from 't-lib-ui/widgets';
import { FormGrid } from 't-lib-ui/structure';
import { TextButton } from 't-lib-ui/widgets';
import { FormGridLine } from 't-lib-ui/structure';
import { getCurrentUser, signUp, googleLogin, appleLogin, AppleCredential, tryEnableMembership, getInvitationPreview } from '../../connector/auth';
import { RestInvitationPreview } from '../../schema-copy/auth-schema';
import { planDisplayNames } from '../../schema-copy/userPlans';
import { createUser } from '../../connector/api';
import { createOnChangeHandler, isAnyFalsish, isAnyTrueish, noWhitespaceTrim, validateEmail, validateInput } from 't-lib-ui/helpers';
import { OrRuler } from 't-lib-ui/structure';
import ExternalLoginButtons, { externalLoginFirst, hasExternalLogin } from '../../components/ExternalLoginButtons';
import DownreadLogo from '../../components/DownreadLogo';
import LegalFooter from '../../components/LegalFooter';
import { PendingSignup, setPendingSignup } from './pending-signup';
import { markAnonMigrationIfNeeded } from '../../connector/anon-migration';

const passwordErrorTexts = {
  missing: 'Please enter a password.',
  short: 'Too short. The password needs to be at least 8 characters long.',
  simple: 'Too simple. Use upper and lower case, digits and/or special characters.',
};

const repeatPasswordErrorTexts: any = {
  ...passwordErrorTexts,
  missing: 'Please repeat the password here.',
  unequal: 'Both passwords must match.',
};

const inviteErrorTexts: Record<string, string> = {
  InvitationInvalid: 'This invitation link is no longer valid.',
  InvitationUsed: 'This invitation has already been fully used.',
  NoCapacity: "We can't accept new sign-ups at the moment. Please try again later.",
};

const Page: React.FC = () => {
  const routerCtx = useContext(RouterContext);
  const appState = useContext(AppState);
  useFlowFormGlobal();

  const [presentLoading, dismissLoading] = useProgressModal();

  // Invitation carried in via the ?i= URL parameter.
  const invitationCode = getLocation(routerCtx).searchParams.get('i') || undefined;
  // Test-signup knobs (e2e only; inert without a valid tag+secret the server checks): ?jurisdiction=EU|US
  // forces data residency, ?confirm=1 keeps the account unconfirmed and sends the (mailbox-diverted)
  // confirmation email so the real confirm flow can be driven.
  const testJurisdiction = getLocation(routerCtx).searchParams.get('jurisdiction') || undefined;
  const testRequireConfirmation = getLocation(routerCtx).searchParams.get('confirm') === '1';
  const [invitePreview, setInvitePreview] = useState<RestInvitationPreview | undefined>(undefined);
  const [inviteError, setInviteError] = useState<string | undefined>(undefined);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<'missing'|'bad'|'dupe'|undefined>(undefined);
  const [existingEmail, setExistingEmail] = useState('');

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<'missing'|'short'|'simple'|undefined>(undefined);

  const [repeatPassword, setRepeatPassword] = useState('');
  const [repeatPasswordError, setRepeatPasswordError] = useState<'unequal'|'missing'|undefined>(undefined);

  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<'ok'|'done'|'working'|'connection'>('ok');
  const trimmedEmailForLinks = noWhitespaceTrim(email);
  const emailQuery = trimmedEmailForLinks ? `?email=${encodeURIComponent(trimmedEmailForLinks)}` : '';
  const emailErrorTexts: Record<string, React.ReactNode> = {
    missing: 'Please enter an email.',
    bad: 'Please enter a valid email.',
    dupe: <>Already registered. <Link to={`/login${emailQuery}`}>Log in instead?</Link><br/><Link to={`/forgot-password${emailQuery}`}>Forgot your password?</Link></>,
  };

  const validationNeededChecker = () => submitted;

  useEffect(() => {
    setTimeout(() => focusFirstElement(), 50);
  }, []);

  useEffect(() => {
    if (!invitationCode) return;
    getInvitationPreview(invitationCode).then(setInvitePreview).catch(() => undefined);
  }, [invitationCode]);

  function validateRepeatPassword(value: string): 'missing'|'unequal'|undefined {
    if (!value)
      return 'missing';
    return password != value ? 'unequal' : undefined;
  }

  function validatePasswordSignup(value: string): 'missing'|'short'|'simple'|undefined {
    if (!value.trim())
      return 'missing';
    const errors = validatePassword(email, value);
    if (!errors.length)
      return undefined;

    if (['passwordNoWhitespace', 'passwordShort', 'passwordLong'].includes(errors[0]))
      return 'short';
    return 'simple';
  }

  function validateEmailWithDupeCheck(email: string): 'bad'|'missing'|'dupe'|undefined {
    const orig = validateEmail(email);
    if (orig)
      return orig;
    return email == existingEmail ? 'dupe' : undefined;
  }

  const signUpClicked = (): boolean => {
    setSubmitted(true);
    const trimmedEmail = noWhitespaceTrim(email);

    // Special trick: a tag can be appended to the email separated by a pipe
    // (e.g. "tim@example.com|tag234") to create a tagged user, which is easy to
    // delete later. An optional third pipe field is a shared secret
    // (e.g. "tim@example.com|loadtest|s3cr3t") that unlocks the no-email-validation
    // signup path (must match the server's TEST_SIGNUP_SECRET). When a valid
    // \w{3,16} tag is present we send the email part as-is (without further email
    // validation) together with the tag and optional secret.
    // The email group is non-greedy so the FIRST pipe splits it off — otherwise a
    // three-field "email|tag|secret" mis-parses (greedy email eats "email|tag", the
    // secret gets read as the tag, and the real secret is dropped).
    const tagMatch = trimmedEmail.match(/^(.+?)\|(\w{3,16})(?:\|(\S+))?$/);
    const signupEmail = tagMatch ? tagMatch[1] : trimmedEmail;
    const tag = tagMatch ? tagMatch[2] : ((document.getElementById('tag-account') as HTMLInputElement)?.value || '');
    const secret = tagMatch ? tagMatch[3] : undefined;

    if (isAnyFalsish(tagMatch ? (setEmailError(undefined), true) : validateInput(trimmedEmail, validateEmail, setEmailError),
        validateInput(password, validatePasswordSignup, setPasswordError),
        validateInput(repeatPassword, validateRepeatPassword, setRepeatPasswordError)))
      return true;

    // Signups don't touch the server here: the account is only created from the accept-terms
    // page, after the user agreed to the ToS and passed the Turnstile (a bot could otherwise
    // create accounts and trigger confirmation emails to arbitrary addresses). The ONE exception
    // is a pipe-trick signup with tag AND secret: the server bypasses turnstile for it (when the
    // secret matches TEST_SIGNUP_SECRET), so it keeps the direct path and e2e/load tooling never
    // sees the accept-terms page. A tag WITHOUT a secret gets no server-side bypass and must go
    // through the turnstile page like everyone else (the tag still rides along).
    if (!(tagMatch && secret)) {
      setPendingSignup({ email: signupEmail, password, invitationCode, tag: tag || undefined });
      redirect(routerCtx, '/accept-terms');
      return true;
    }

    setStatus('working');
    setBusy(true);
    setInviteError(undefined);
    markAnonMigrationIfNeeded();
    signUp(signupEmail, password, 'user', undefined, undefined, tag, invitationCode, secret, testJurisdiction, testRequireConfirmation)
    .then(async (authData) => {
      setBusy(false);
      if (authData.status == 'unconfirmed') {
        appState.setUnconfirmed(authData);
        return Promise.resolve();
      }
      else if (authData.status == 'confirmed') { // test mode: account confirmed immediately, but user not yet created
        const user = await createUser(authData, 'user');
        const {authData: newAuthData, user: newUser} = await tryEnableMembership(authData, {memberId: user.id, teamId: user.teamId});
        appState.setLoggedIn(newAuthData, newUser ?? user);
        return;
      }
      else
        throw new Error('No membership selected after signup');
    })
    .then(() => {
      setStatus('done');
    }).catch((e: any) => {
      console.log('Signup error:', e);
      if (e instanceof FetchError && inviteErrorTexts[e.type]) {
        setInviteError(inviteErrorTexts[e.type]);
        setStatus('ok');
      }
      else if (e instanceof FetchError && e.type == 'UserExists') {
        setExistingEmail(trimmedEmail);
        setEmailError('dupe');
        setStatus('ok');
      }
      else
        setStatus('connection');
      setBusy(false);
    });
    return true;
  };

  const statusFieldError = isAnyTrueish(emailError, passwordError, repeatPasswordError);

  if (status == 'done') {
    if (appState.state.authData?.status == 'unconfirmed')
      return redirect(routerCtx, '/confirm-email');
    else if (appState.state.authData?.status == 'confirmed') // this case is highly unlikely outside a test environment
      return redirect(routerCtx, '/a');
    else
      throw new Error('Unknown user state after signup');
  }

  // Google and Apple sign-ups. pending: what the ToS step needs (the identity is usually new).
  const externalSignup = async (provider: string, doLogin: () => ReturnType<typeof googleLogin>, pending: PendingSignup) => {
    presentLoading({
      message: `Signing up with ${provider}...`,
      timeout: 10000,
    });

    try {
      setInviteError(undefined);
      markAnonMigrationIfNeeded();
      const {authData, user} = await doLogin();

      // If no user exists yet, create one (new signup)
      if (!user) {
        const newUser = await createUser(authData, 'user');
        const {authData: newAuthData, user: enabledUser} = await tryEnableMembership(authData, {memberId: newUser.id, teamId: newUser.teamId});
        dismissLoading();
        appState.setLoggedIn(newAuthData, enabledUser);
        setStatus('done');
      }
      else {
        // User already exists (returning user)
        dismissLoading();
        appState.setLoggedIn(authData, user);
        setStatus('done');
      }
    } catch (e) {
      dismissLoading();
      if (e instanceof FetchError && e.type == 'TermsNotAccepted') {
        // New user: the server only creates the account after the ToS step.
        setPendingSignup({ ...pending, invitationCode });
        return redirect(routerCtx, '/accept-terms');
      }
      if (e instanceof FetchError && inviteErrorTexts[e.type])
        setInviteError(inviteErrorTexts[e.type]);
      else
        setStatus('connection');
    }
  };

  const handleGoogleSuccess = (credential: string) => externalSignup('Google', () => googleLogin(credential, invitationCode), { googleCredential: credential });
  const handleAppleSuccess = (credential: AppleCredential) => externalSignup('Apple', () => appleLogin(credential, invitationCode), { appleCredential: credential });

  const handleExternalError = (error: string) => {
    console.error('External signup error:', error);
    setStatus('connection');
  };

  const externalLogins = hasExternalLogin && <>
    {!externalLoginFirst && <div className="authDivider"><OrRuler /></div>}
    <ExternalLoginButtons verb="Sign up" onGoogle={handleGoogleSuccess} onApple={handleAppleSuccess} onError={handleExternalError} />
    {externalLoginFirst && <div className="authDivider"><OrRuler /></div>}
  </>;

  return (
    <FlowForm main={true}>
      <div className="authPage">
        <div className="authCard signupCard">
          <div className="authLogo">
            <a href="https://downread.com" className="authLogoLink"><DownreadLogo size={48} showText /><span className="betaBadge" aria-label="Beta">beta</span></a>
          </div>
          <div className="signupIntro">
            {invitePreview
              ? <>You've been invited to <strong>Downread {(planDisplayNames as Record<string, string>)[invitePreview.planCode] || invitePreview.planCode}</strong>. Create your account below.</>
              : <>Create your free Downread account.</>}
          </div>

          {invitePreview && (
            <Note type='info'>Invitation: {invitePreview.name}</Note>
          )}

          {inviteError && <Note type='error'>{inviteError}</Note>}

          {status == 'connection' && <Note type='error'>Can't connect to server. Please try again later.</Note>}

          {externalLoginFirst && externalLogins}

          <FormGrid>
            <FormGridLine span={2} className='signupInputRow'>
              <Input label='Email' layout='singleCol' errorId={emailError} errorTexts={emailErrorTexts} className='half-margin-bottom email' type="email" placeholder="email@domain.com" enterKeyHint="next" maxLength={EmailLength} onInput={createOnChangeHandler(setEmail, validateEmailWithDupeCheck, setEmailError, validationNeededChecker)}/>
            </FormGridLine>
            <FormGridLine span={2} className='signupInputRow'>
              <PasswordInput label='Password' layout='singleCol' errorId={passwordError} errorTexts={passwordErrorTexts} className='half-margin-bottom password1' enterKeyHint="next" maxLength={PasswordLength} onInput={createOnChangeHandler(setPassword, validatePasswordSignup, setPasswordError, validationNeededChecker)}/>
            </FormGridLine>
            <FormGridLine span={2} className='signupInputRow'>
              <PasswordInput label='Repeat Password' layout='singleCol' errorId={repeatPasswordError} errorTexts={repeatPasswordErrorTexts} className='half-margin-bottom password2' enterKeyHint="enter" maxLength={PasswordLength} onInput={createOnChangeHandler(setRepeatPassword, validateRepeatPassword, setRepeatPasswordError, validationNeededChecker)}/>
            </FormGridLine>
            <FormGridLine span={2} align='center' className='signupSubmitRow'>
              <TextButton className='next' onClick={signUpClicked} enabled={statusFieldError || busy ? 'disabled' : 'enabled'} label='Sign Up' type='major' busy={busy} />
            </FormGridLine>
            <input type="hidden" id="tag-account" value=""/>
          </FormGrid>

          {!externalLoginFirst && externalLogins}

          <div className="authFooter">
            Already have an account? <Link to={'/login'}>Log in here</Link>
          </div>
        </div>
        {process.env.REACT_APP_BUILD_NUMBER && <div className="buildNumber">b{process.env.REACT_APP_BUILD_NUMBER}</div>}
      </div>
      <LegalFooter />
    </FlowForm>
  );
};

export default Page;
